import { useState, useCallback } from 'react';
import { API_URL } from '../constants/api';
import { trackEvent, Events } from '../lib/posthog';

export function useAskBirdie(deviceId, currentSwingId, analysisResult) {
  const [showAskBirdie, setShowAskBirdie] = useState(false);
  const [askBirdieMessages, setAskBirdieMessages] = useState([]);
  const [askBirdieInput, setAskBirdieInput] = useState('');
  const [askBirdieLoading, setAskBirdieLoading] = useState(false);
  const [questionsRemaining, setQuestionsRemaining] = useState(5);

  // Check if Ask Birdie is ready (has required data)
  const isReady = Boolean(deviceId && currentSwingId && analysisResult);

  const sendMessage = useCallback(async () => {
    if (!askBirdieInput.trim() || !currentSwingId || !deviceId || askBirdieLoading) return;
    if (questionsRemaining <= 0) {
      alert('You\'ve used all 5 free follow-up questions for this analysis.');
      return;
    }

    const userMessage = askBirdieInput.trim();
    setAskBirdieInput('');
    setAskBirdieMessages(prev => [...prev, { role: 'user', message: userMessage }]);
    setAskBirdieLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/coaching/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          deviceId,
          swingId: currentSwingId,
          message: userMessage
        })
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error');
      }

      const data = await response.json();

      if (response.ok && data.message) {
        setAskBirdieMessages(prev => [...prev, { role: 'assistant', message: data.message }]);
        setQuestionsRemaining(data.questionsRemaining ?? questionsRemaining - 1);
        trackEvent(Events.ASK_BIRDIE_QUESTION_SENT, { questionsRemaining: data.questionsRemaining });
      } else {
        const errorMsg = data.error || 'Sorry, I couldn\'t process that question. Please try again.';
        setAskBirdieMessages(prev => [...prev, { role: 'assistant', message: errorMsg }]);
      }
    } catch (error) {
      console.error('Ask Birdie error:', error);
      let errorMessage = 'Sorry, something went wrong. Please try again.';
      if (error.message?.includes('fetch')) {
        errorMessage = 'Unable to connect. Please check your connection.';
      }
      setAskBirdieMessages(prev => [...prev, { role: 'assistant', message: errorMessage }]);
    } finally {
      setAskBirdieLoading(false);
    }
  }, [askBirdieInput, currentSwingId, askBirdieLoading, questionsRemaining, deviceId]);

  const openAskBirdie = useCallback(async () => {
    setShowAskBirdie(true);
    trackEvent(Events.ASK_BIRDIE_OPENED);

    if (!currentSwingId) {
      console.warn('Ask Birdie opened but no currentSwingId available');
      return;
    }

    if (!deviceId) {
      console.warn('Ask Birdie opened but no deviceId available');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/coaching/conversation/${currentSwingId}?deviceId=${deviceId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.messages && Array.isArray(data.messages)) {
        setAskBirdieMessages(data.messages);
        setQuestionsRemaining(data.questionsRemaining ?? 5);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
      // Don't show error to user - just start with empty conversation
      setAskBirdieMessages([]);
      setQuestionsRemaining(5);
    }
  }, [currentSwingId, deviceId]);

  const getSuggestedQuestions = useCallback(() => {
    if (!analysisResult) return [];
    const questions = [];

    if (analysisResult.whatsCostingStrokes?.[0]?.issue) {
      questions.push(`Tell me more about my ${analysisResult.whatsCostingStrokes[0].issue.toLowerCase()}`);
    }
    if (analysisResult.criticalFix?.drill?.name) {
      questions.push(`Can you explain the ${analysisResult.criticalFix.drill.name} drill in more detail?`);
    }
    if (analysisResult.projectedHandicap?.range) {
      questions.push('What would it take to drop my handicap by 2-3 strokes?');
    }
    if (analysisResult.criticalFix?.fix) {
      questions.push('What\'s the most common mistake people make when working on this fix?');
    }
    if (questions.length < 4) {
      questions.push('What should I focus on during my next range session?');
    }

    return questions.slice(0, 4);
  }, [analysisResult]);

  const resetAskBirdie = useCallback(() => {
    setQuestionsRemaining(5);
    setAskBirdieMessages([]);
  }, []);

  return {
    showAskBirdie,
    setShowAskBirdie,
    askBirdieMessages,
    setAskBirdieMessages,
    askBirdieInput,
    setAskBirdieInput,
    askBirdieLoading,
    questionsRemaining,
    setQuestionsRemaining,
    sendMessage,
    openAskBirdie,
    getSuggestedQuestions,
    resetAskBirdie,
    isReady,
  };
}
